import type { ButtonHTMLAttributes, ReactNode } from "react";

type Tone = "primary" | "line" | "attention" | "quiet";

/**
 * Buttons are chalk lines, not cards: a filled block or a rule. No shadows, no gradients,
 * and no accent colour — Sun Glare means "live", and a button is not a live thing.
 * `attention` is for the one case that is genuinely a warning: raising a dispute.
 *
 * `quiet` is a button that has to sit in a row with the others without competing with them —
 * a show/hide toggle beside a real action. It keeps the same box as every other tone, which
 * is the point: rendered as bare underlined text it sat on a different baseline and made the
 * row look thrown together.
 */
export function Button({
  tone = "line",
  children,
  ...rest
}: { tone?: Tone; children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>) {
  const base = {
    minHeight: 48,
    borderRadius: "var(--radius-control)",
    fontWeight: 600,
    letterSpacing: "0.02em",
    padding: "0 18px",
  } as const;

  const tones = {
    primary: {
      background: "var(--color-line)",
      color: "var(--color-ground)",
      border: "none",
      fontWeight: 700,
    },
    line: {
      background: "transparent",
      color: "var(--color-ink)",
      border: "2px solid var(--color-line)",
    },
    attention: {
      background: "transparent",
      color: "var(--color-attention)",
      border: "2px solid var(--color-attention)",
    },
    // Transparent border rather than none, so its box matches the others exactly and the
    // row's baseline does not shift when it appears.
    quiet: {
      background: "transparent",
      color: "var(--color-ink-muted)",
      border: "2px solid transparent",
      padding: "0 8px",
    },
  } as const;

  return (
    <button
      {...rest}
      className={`disabled:opacity-40 ${rest.className ?? ""}`}
      style={{ ...base, ...tones[tone], ...rest.style }}
    >
      {children}
    </button>
  );
}

/**
 * An action that is a glyph rather than a sentence.
 *
 * For the things that sit beside a heading — share, manage — where a full bordered button
 * was a slab of chrome competing with the title it belonged to. No border and no fill: it is
 * ink on the page like everything else, and it darkens to full strength on hover so it is
 * still discoverably a control.
 *
 * 44px is not negotiable. The glyph is 20, and the padding is what makes the thing tappable
 * on a phone — an icon button drawn at its own size is a target only a mouse can hit.
 *
 * `label` is required and becomes the accessible name. An icon on its own says nothing to a
 * screen reader, and `title` is not a substitute — it never reaches a touch device.
 */
export const iconButtonClass =
  "flex items-center justify-center text-ink-muted transition-colors hover:text-ink disabled:opacity-40";

export const iconButtonStyle = {
  width: 44,
  height: 44,
  borderRadius: "var(--radius-control)",
  background: "transparent",
  border: "none",
} as const;

export function IconButton({
  label,
  children,
  ...rest
}: { label: string; children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      aria-label={label}
      title={label}
      className={`${iconButtonClass} ${rest.className ?? ""}`}
      style={{ ...iconButtonStyle, ...rest.style }}
    >
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="mb-6 block">
      <span className="field-label">{label}</span>
      {children}
      {hint ? <span className="mt-1.5 block text-sm text-ink-muted">{hint}</span> : null}
    </label>
  );
}

export const inputStyle = {
  width: "100%",
  minHeight: 48,
  padding: "0 12px",
  background: "transparent",
  color: "var(--color-ink)",
  border: "2px solid var(--color-line)",
  borderRadius: "var(--radius-control)",
} as const;

/** A row of pick-one or pick-many chips. Selection is ink weight, never colour. */
export function Chips<T extends string>({
  options,
  selected,
  onToggle,
}: {
  options: { value: T; label: string }[];
  selected: T[];
  onToggle: (value: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const on = selected.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onToggle(option.value)}
            aria-pressed={on}
            style={{
              minHeight: 44,
              padding: "0 14px",
              borderRadius: "var(--radius-control)",
              border: `2px solid ${on ? "var(--color-line)" : "var(--color-line-muted)"}`,
              fontSize: 14,
              background: on ? "var(--color-line)" : "transparent",
              color: on ? "var(--color-ground)" : "var(--color-ink-muted)",
              fontWeight: on ? 600 : 400,
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A pick-one control that stays out of the way. Chrome is never coloured, so a filter that
 * is doing something looks no different from one that is not — which is right: the list
 * underneath is the feedback, and a row of highlighted selectors would shout over it.
 *
 * Ruled like every other control on the sheet, with its own chevron: the native one is a
 * different size on every phone.
 */
export function Selector({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  label?: string;
}) {
  return (
    <span className="relative inline-flex">
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        className="appearance-none bg-transparent pl-3 pr-9 text-sm font-semibold"
        style={{
          borderRadius: "var(--radius-control)",
          border: "2px solid var(--color-line)",
          color: "var(--color-ink)",
          minHeight: 48,
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
      >
        <path d="M5 9l7 7 7-7" />
      </svg>
    </span>
  );
}

/**
 * Two or three mutually exclusive options that are worth seeing all at once — Doubles and
 * Singles, Mine and Everyone. The chosen one is filled with ink.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  label: string;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={`flex border-2 ${className ?? ""}`}
      style={{ borderColor: "var(--color-line)" }}
    >
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(option.value)}
            className="flex-1 px-3 text-sm font-semibold"
            style={{
              minHeight: 44,
              background: on ? "var(--color-line)" : "transparent",
              color: on ? "var(--color-ground)" : "var(--color-ink)",
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** A full-width message in the app's voice. Never "an error occurred". */
export function Notice({
  children,
  tone = "muted",
}: {
  children: ReactNode;
  tone?: "muted" | "attention";
}) {
  return (
    <p
      className="my-4 text-sm"
      style={{ color: tone === "attention" ? "var(--color-attention)" : "var(--color-ink-muted)" }}
    >
      {children}
    </p>
  );
}
